import apiClient, { publicApiClient } from './client';
import type {
  Plan,
  Subscription,
  Quota,
  UsageResponse,
  Invoice,
  PlansResponse,
  SubscriptionResponse,
  QuotaResponse,
  InvoicesResponse,
  BillingInterval,
  DailyActivity,
  UsageActivityResponse,
  PlanChangePreview,
  PlanChangePreviewResponse,
  CheckoutSessionResponse,
  CustomerPortalResponse,
} from '../types';

/**
 * Fetch all available subscription plans.
 * GET /billing/plans (public endpoint)
 */
export async function getPlans(): Promise<Plan[]> {
  const response = await publicApiClient.get<PlansResponse>('/billing/plans');
  return response.data.plans;
}

/**
 * Get the current user's subscription.
 * GET /billing/subscription (requires auth)
 */
export async function getSubscription(): Promise<Subscription | null> {
  try {
    const response = await apiClient.get<SubscriptionResponse>('/billing/subscription');
    return response.data.subscription;
  } catch (error) {
    // 404 means no subscription exists
    if ((error as { response?: { status: number } }).response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Subscribe to a plan.
 * POST /billing/subscribe (requires auth)
 */
export async function subscribe(
  planId: string,
  interval: BillingInterval
): Promise<Subscription> {
  const response = await apiClient.post<SubscriptionResponse>('/billing/subscribe', {
    plan_id: planId,
    interval,
  });
  return response.data.subscription;
}

/**
 * Cancel the current subscription.
 * POST /billing/cancel (requires auth)
 */
export async function cancelSubscription(immediately = false): Promise<void> {
  await apiClient.post('/billing/cancel', {
    immediately,
  });
}

/**
 * Get the current user's quota status.
 * GET /billing/quota (requires auth)
 */
export async function getQuota(): Promise<Quota | null> {
  try {
    const response = await apiClient.get<QuotaResponse>('/billing/quota');
    return response.data.quota;
  } catch (error) {
    // 404 means no quota exists
    if ((error as { response?: { status: number } }).response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Get usage statistics for a date range.
 * GET /billing/usage (requires auth)
 */
export async function getUsage(start?: string, end?: string): Promise<UsageResponse> {
  const params: Record<string, string> = {};
  if (start) params.start = start;
  if (end) params.end = end;

  const response = await apiClient.get<UsageResponse>('/billing/usage', { params });
  return response.data;
}

/**
 * Get invoice/payment history.
 * GET /billing/invoices (requires auth)
 */
export async function getInvoices(
  limit = 20,
  offset = 0
): Promise<{ invoices: Invoice[]; total: number }> {
  const response = await apiClient.get<InvoicesResponse>('/billing/invoices', {
    params: { limit, offset },
  });
  return {
    invoices: response.data.invoices,
    total: response.data.total,
  };
}

/**
 * Get the last 7 days of transcription activity.
 * GET /billing/usage/activity (requires auth)
 */
export async function getUsageActivity(): Promise<DailyActivity[]> {
  const response = await apiClient.get<UsageActivityResponse>('/billing/usage/activity');
  return response.data.activity;
}

/**
 * Preview a plan change with proration details.
 * POST /billing/subscription/preview (requires auth)
 */
export async function previewPlanChange(
  planId: string,
  interval: BillingInterval
): Promise<PlanChangePreview> {
  const response = await apiClient.post<PlanChangePreviewResponse>(
    '/billing/subscription/preview',
    {
      plan_id: planId,
      interval,
    }
  );
  return response.data.preview;
}

/**
 * Apply a plan change (upgrade immediately, downgrade at period end).
 * POST /billing/subscription/change (requires auth)
 */
export async function applyPlanChange(
  planId: string,
  interval: BillingInterval
): Promise<Subscription> {
  const response = await apiClient.post<SubscriptionResponse>(
    '/billing/subscription/change',
    {
      plan_id: planId,
      interval,
    }
  );
  return response.data.subscription;
}

/**
 * Cancel a pending plan change (downgrade).
 * DELETE /billing/subscription/pending (requires auth)
 */
export async function cancelPendingChange(): Promise<void> {
  await apiClient.delete('/billing/subscription/pending');
}

/**
 * Create a Stripe Checkout session for a new subscription.
 * POST /billing/checkout (requires auth)
 */
export async function createCheckoutSession(
  planId: string,
  interval: BillingInterval,
  successUrl?: string,
  cancelUrl?: string,
  promotionCode?: string
): Promise<CheckoutSessionResponse> {
  const response = await apiClient.post<CheckoutSessionResponse>('/billing/checkout', {
    plan_id: planId,
    interval,
    success_url: successUrl || `${window.location.origin}/billing?checkout=success`,
    cancel_url: cancelUrl || `${window.location.origin}/pricing?checkout=cancelled`,
    ...(promotionCode ? { promotion_code: promotionCode } : {}),
  });
  return response.data;
}

/**
 * Create a Stripe Customer Portal session for managing subscription.
 * POST /billing/portal (requires auth)
 */
export async function createCustomerPortal(
  returnUrl?: string
): Promise<CustomerPortalResponse> {
  const response = await apiClient.post<CustomerPortalResponse>('/billing/portal', {
    return_url: returnUrl || `${window.location.origin}/billing`,
  });
  return response.data;
}
